// Finding a seed's Audiotool projects, and marking a project as a seed's.
//
// The link itself is shared/audiotool/link.ts — a tag and a description line on
// the project. This is the half that talks to Audiotool, on nothing but the
// SDK's ProjectService, so the browser (viz, via @audiotool-core) and the
// extension run the same code. No Live imports belong here.

import type { AudiotoolClient } from "@audiotool/nexus";

import {
  isLinkedTo,
  linkTag,
  parseLink,
  studioUrl,
  withLink,
  withLinkTag,
  type SeedLink,
} from "../../../shared/audiotool/link.js";
import { errorText, isSdkError } from "./uploads.js";

export type ProjectsClient = Pick<AudiotoolClient, "projects">;

export interface ProjectRow {
  name: string;
  displayName: string;
  updated: Date | null;
}

export interface LinkedProject extends ProjectRow {
  studioUrl: string;
  /** The seed it was last synced with; the root when only the tag says so. */
  link: SeedLink;
}

const PAGE_SIZE = 50;
/** How far the fallback scan reads back through a producer's projects. */
const SCAN_PAGES = 6;

/** A project as the SDK returns it — only the fields read here. */
interface ProjectLike {
  name: string;
  displayName: string;
  description: string;
  tags: string[];
  updateTime?: { toDate(): Date };
}

function row(p: ProjectLike): ProjectRow {
  return { name: p.name, displayName: p.displayName || "Untitled", updated: p.updateTime?.toDate() ?? null };
}

/** The producer's most recently edited projects. */
export async function listProjects(client: ProjectsClient): Promise<ProjectRow[]> {
  const res = await client.projects.listProjects({ pageSize: PAGE_SIZE, orderBy: "project.update_time desc" });
  if (isSdkError(res)) throw new Error(`Couldn't list your Audiotool projects: ${errorText(res)}`);
  return res.projects.map(row);
}

/** One project, if this account can see it — for a link somebody pasted. */
export async function getProjectRow(client: ProjectsClient, projectName: string): Promise<ProjectRow> {
  const res = await client.projects.getProject({ name: projectName });
  if (isSdkError(res) || !res.project) {
    throw new Error(`Couldn't open that Audiotool project${isSdkError(res) ? ` (${errorText(res)})` : ""}. Is it yours, and is the link right?`);
  }
  return row(res.project);
}

/**
 * Every project on this account linked to the song `rootSeedId`, most recently
 * edited first.
 *
 * Asks Audiotool to filter by the tag first. If it can't — or finds nothing,
 * which is also what a tag it silently dropped looks like — reads back through
 * the recent projects and matches here, on the tag or the description line.
 */
export async function findLinkedProjects(client: ProjectsClient, rootSeedId: string): Promise<LinkedProject[]> {
  const filtered = await client.projects.listProjects({
    filter: `"${linkTag(rootSeedId)}" in project.tags`,
    pageSize: PAGE_SIZE,
    orderBy: "project.update_time desc",
  });
  const hits = isSdkError(filtered) ? [] : filtered.projects.filter((p) => isLinkedTo(p, rootSeedId));
  if (hits.length > 0) return hits.map(linked);

  const found: LinkedProject[] = [];
  let pageToken = "";
  for (let page = 0; page < SCAN_PAGES; page++) {
    const res = await client.projects.listProjects({ pageSize: PAGE_SIZE, pageToken, orderBy: "project.update_time desc" });
    if (isSdkError(res)) throw new Error(`Couldn't list your Audiotool projects: ${errorText(res)}`);
    for (const p of res.projects) if (isLinkedTo(p, rootSeedId)) found.push(linked(p));
    if (!res.nextPageToken) break;
    pageToken = res.nextPageToken;
  }
  return found;
}

function linked(p: ProjectLike): LinkedProject {
  return { ...row(p), studioUrl: studioUrl(p.name), link: parseLink(p)! };
}

/**
 * Mark `projectName` as `link.seedId`'s: the song's tag, and the description
 * line pointing at that seed. Merges — the producer's own tags and description
 * stay. If Audiotool refuses the tag, the description alone is written; it is
 * enough to find the project again.
 */
export async function stampProject(client: ProjectsClient, projectName: string, link: SeedLink, origin?: string): Promise<void> {
  const got = await client.projects.getProject({ name: projectName });
  if (isSdkError(got) || !got.project) {
    throw new Error(`Couldn't read the Audiotool project: ${isSdkError(got) ? errorText(got) : "not found"}`);
  }
  const { description, tags } = got.project;
  const nextDescription = withLink(description, link, origin);
  const nextTags = withLinkTag(tags, link.rootSeedId);
  if (nextDescription === description && nextTags.length === tags.length) return;

  const both = await client.projects.updateProject({
    project: { name: projectName, description: nextDescription, tags: nextTags },
    updateMask: { paths: ["description", "tags"] },
  });
  if (!isSdkError(both)) return;
  const described = await client.projects.updateProject({
    project: { name: projectName, description: nextDescription },
    updateMask: { paths: ["description"] },
  });
  if (isSdkError(described)) throw new Error(`Couldn't link the Audiotool project: ${errorText(described)}`);
}

/**
 * `stampProject`, for callers whose real work is already done (an export, an
 * upload): a project that can't be linked is worth a line, never a failure.
 * Returns that line, or null when the project is linked.
 */
export async function linkQuietly(client: ProjectsClient, projectName: string, link: SeedLink, origin?: string): Promise<string | null> {
  try {
    await stampProject(client, projectName, link, origin);
    return null;
  } catch (e) {
    return `The Audiotool project couldn't be linked to the seed, so the seed's page won't find it (${errorText(e)}).`;
  }
}
